import Sidebar from 'pages/sideBar/SideBar';
import React, { useEffect, useState, useMemo } from 'react';
import '../app.css';
import imgReviews from '../../../../assets/images/order.png';
import PastOrdersIMg from '../../../../assets/images/pastorder.png';
import axios from 'axios';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import NoDataImage from '../../../../assets/images/nodataImg.png';
import {
    ActiveOrderCard,
    CompletedOrderRow,
    CompletedOrderCard,
    OrderDetailModal,
    getLocationName,
} from 'components/orderComponets/ActiveOrders';
import { getReadableOrderId } from 'pages/utils/HelperFun';

const apiURL = 'https://production.washta.com/api/seller/pastorder';

// ── Date helpers ───────────────────────────────────────────────────────────────
const toISODate = (d) => d.toISOString().split('T')[0];
const today = () => toISODate(new Date());
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return toISODate(d); };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); return toISODate(d); };
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

function OrderDashboard() {
    const [data, setData] = useState([]);
    const [pastOrderData, setPastOrderData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('ordersViewMode') || 'list');
    const [activeDatePill, setActiveDatePill] = useState('today');
    const [modalOrder, setModalOrder] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        location: 'all',
        dateFrom: today(),
        dateTo: today(),
    });

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchData = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            const res = await axios.get('https://production.washta.com/api/seller/latestOrders', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(res.data.data);
        } catch (err) { console.log(err.message); }
        finally { setLoading(false); }
    };

    const fetchPastOrder = async () => {
        const token = localStorage.getItem('accessToken');
        try {
            const res = await axios.get(apiURL, { headers: { Authorization: `Bearer ${token}` } });
            setPastOrderData(res.data.data);
        } catch (err) { console.log(err.message); }
    };

    useEffect(() => { window.scrollTo(0, 0); fetchData(); fetchPastOrder(); }, []);

    // ── View mode persist ──────────────────────────────────────────────────────
    const setView = (mode) => { setViewMode(mode); localStorage.setItem('ordersViewMode', mode); };

    // ── Date quick pills ───────────────────────────────────────────────────────
    const applyDatePill = (pill) => {
        setActiveDatePill(pill);
        if (pill === 'today')     setFilters(f => ({ ...f, dateFrom: today(),      dateTo: today() }));
        if (pill === 'yesterday') setFilters(f => ({ ...f, dateFrom: yesterday(),  dateTo: yesterday() }));
        if (pill === 'week')      setFilters(f => ({ ...f, dateFrom: weekStart(),  dateTo: today() }));
        if (pill === 'month')     setFilters(f => ({ ...f, dateFrom: monthStart(), dateTo: today() }));
        if (pill === 'all')       setFilters(f => ({ ...f, dateFrom: '',           dateTo: '' }));
    };

    // ── Filter logic ───────────────────────────────────────────────────────────
    const applyFilters = (list) => {
        if (!list) return [];
        return list.filter((item) => {
            if (item.billingStatus === 'non-paid') return false;

            const s = filters.search.toLowerCase();
            const locationLabel = getLocationName(item, item?.location?.text || 'Location not provided');
const readableId = item._id ? getReadableOrderId(String(item._id), 'WS').toLowerCase() : '';
const matchesSearch = !filters.search ||
    (item._id && String(item._id).toLowerCase().includes(s)) ||
    readableId.includes(s) ||
    readableId.replace('-', '').includes(s.replace('-', '')) ||
    (item.promoCode && String(item.promoCode).toLowerCase().includes(s)) ||
    (item.shopId?.shopName && String(item.shopId.shopName).toLowerCase().includes(s)) ||
    (locationLabel && String(locationLabel).toLowerCase().includes(s)) ||
    (item.vehicleId?.vehiclePlateNumber && String(item.vehicleId.vehiclePlateNumber).toLowerCase().includes(s));
            let matchesStatus = true;
            if (filters.status !== 'all') {
                if (filters.status === 'expired')  matchesStatus = item.isExpired === true;
                else if (filters.status === 'ongoing') matchesStatus = item.status === 'ongoing' && !item.isExpired;
                else matchesStatus = item.status === filters.status;
            }

            const matchesLocation = filters.location === 'all' || locationLabel === filters.location;

            let matchesDate = true;
            const orderDate = new Date(item.createdAt).toISOString().split('T')[0];
            if (filters.dateFrom) matchesDate = orderDate >= filters.dateFrom;
            if (filters.dateTo)   matchesDate = matchesDate && orderDate <= filters.dateTo;

            return matchesSearch && matchesStatus && matchesLocation && matchesDate;
        });
    };

    const filteredActive = useMemo(() => applyFilters(data), [data, filters]);
    const filteredPast   = useMemo(() => applyFilters(pastOrderData).filter(i => i.status === 'completed'), [pastOrderData, filters]);
    const locationOptions = useMemo(() => {
        const seen = new Set();
        const items = [];
        [...(data || []), ...(pastOrderData || [])].forEach((item) => {
            const label = getLocationName(item, item?.location?.text || 'Location not provided');
            if (label && label !== 'Location not provided' && !seen.has(label)) {
                seen.add(label);
                items.push(label);
            }
        });
        return items.sort((a, b) => a.localeCompare(b));
    }, [data, pastOrderData]);

    const totalOrders = filteredActive.length + filteredPast.length;



    // ── Status update ──────────────────────────────────────────────────────────
    const updateOrderStatus = async (id, status) => {
        const token = localStorage.getItem('accessToken');
        try {
            await axios.patch(`https://production.washta.com/api/seller/orderStatus/${id}`, { id, status }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (status === 'completed') {
                setData((prev) => prev.filter((order) => order._id !== id));
                setPastOrderData((prev) => {
                    const updated = prev.filter((order) => order._id !== id);
                    const completedOrder = prev.find((order) => order._id === id);
                    return completedOrder ? [{ ...completedOrder, status: 'completed' }, ...updated] : updated;
                });
            }
            fetchData();
            fetchPastOrder();
        } catch (error) { console.error('Error updating order status:', error); }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setActiveDatePill('');
    };

    const clearFilters = () => {
        setFilters({ search: '', status: 'all', location: 'all', dateFrom: today(), dateTo: today() });
        setActiveDatePill('today');
    };

    // ── Pill style helper ──────────────────────────────────────────────────────
    const pillStyle = (active) => ({
        fontSize: '10px', padding: '3px 10px', borderRadius: '20px', cursor: 'pointer', border: '1px solid',
        borderColor: active ? '#6B5FE4' : '#ddd',
        background: active ? '#6B5FE4' : '#fff',
        color: active ? '#fff' : '#888',
    });

    // ── Shared input style ─────────────────────────────────────────────────────
    const inputStyle = {
        width: '100%', fontSize: '11px', color: '#111', border: '1px solid #ddd',
        borderRadius: '7px', padding: '5px 8px', background: '#fff', fontFamily: 'inherit',
        outline: 'none',
    };
    return (
        <div className='mainDiv-all-sideBar'>
            <div className='div-sidebar'><Sidebar /></div>
            <div className='div-Dashbaord'>
                <h1 className='h1-all-heading'>Orders</h1>

                {/* ── FILTER BAR ── */}
                <div style={{ background: '#f8f7ff', border: '1px solid #e0dbff', borderRadius: '12px', padding: '12px 14px', marginBottom: '20px' }}>
                    {/* Row 1 */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 2, minWidth: '160px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>Search</span>
                            <input
                            className="custom-input"
                                style={inputStyle}
                                placeholder="Order ID"
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1.4, minWidth: '140px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>Location</span>
                            <select style={inputStyle} name="location" value={filters.location} onChange={handleFilterChange}>
                                <option value="all">All Locations</option>
                                {locationOptions.map((location) => (
                                    <option key={location} value={location}>{location}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '120px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>Status</span>
                            <select style={inputStyle} name="status" value={filters.status} onChange={handleFilterChange}>
                                <option value="all">All Statuses</option>
                                <option value="ongoing">Ongoing</option>
                                <option value="inprocess">In Process</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 2 — date range + quick pills */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>From</span>
                                <input style={{ ...inputStyle, width: '130px' }} type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
                            </div>
                            <div style={{ paddingBottom: '6px', color: '#bbb', fontSize: '12px' }}>→</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>To</span>
                                <input style={{ ...inputStyle, width: '130px' }} type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
                            </div>
                        </div>

                        {/* Quick pills */}
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', paddingTop: '16px', flexWrap: 'wrap' }}>
                            {[
                                { key: 'today',     label: 'Today' },
                                { key: 'yesterday', label: 'Yesterday' },
                                { key: 'week',      label: 'This Week' },
                                { key: 'month',     label: 'This Month' },
                                { key: 'all',       label: 'All time' },
                            ].map(p => (
                                <span key={p.key} style={pillStyle(activeDatePill === p.key)} onClick={() => applyDatePill(p.key)}>
                                    {p.label}
                                </span>
                            ))}
                        </div>

                        <button
                            onClick={clearFilters}
                            style={{ marginLeft: 'auto', fontSize: '10px', color: '#888', border: '1px solid #ddd', borderRadius: '7px', padding: '5px 10px', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {/* ── RESULTS COUNT + VIEW TOGGLE ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                        <strong style={{ color: '#111' }}>{totalOrders}</strong> orders
                        {filters.dateFrom && <span> · {filters.dateFrom}{filters.dateTo !== filters.dateFrom ? ` → ${filters.dateTo}` : ''}</span>}
                    </div>
                    <div style={{ display: 'flex', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                        {[
                            { mode: 'list', icon: <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor"><rect y="0" width="11" height="2" rx="1"/><rect y="4.5" width="11" height="2" rx="1"/><rect y="9" width="11" height="2" rx="1"/></svg>, label: 'List' },
                            { mode: 'cards', icon: <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor"><rect width="4.5" height="4.5" rx="1"/><rect x="6.5" width="4.5" height="4.5" rx="1"/><rect y="6.5" width="4.5" height="4.5" rx="1"/><rect x="6.5" y="6.5" width="4.5" height="4.5" rx="1"/></svg>, label: 'Cards' },
                        ].map(v => (
                            <button
                                key={v.mode}
                                onClick={() => setView(v.mode)}
                                style={{
                                    fontSize: '10px', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '5px',
                                    background: viewMode === v.mode ? '#6B5FE4' : '#fff',
                                    color: viewMode === v.mode ? '#fff' : '#888',
                                    border: 'none', borderRight: v.mode === 'list' ? '1px solid #ddd' : 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                {v.icon} {v.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ════════════════════════════
                    ACTIVE ORDERS
                ════════════════════════════ */}
                {(loading || filteredActive.length > 0) && (
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#111', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <PulseDot />
                            Active Orders
                            <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '100px', background: '#fde8e8', color: '#c62828' }}>
                                {filteredActive.length} {filteredActive.length === 1 ? 'order' : 'orders'}
                            </span>
                        </div>

                        {loading ? <LoadingSkeleton /> : filteredActive.map((item) => (
                            <ActiveOrderCard
                                key={item._id}
                                data={item}
                                Accpet={() => updateOrderStatus(item._id, 'inprocess')}
                                Refuse={() => updateOrderStatus(item._id, 'cancelled')}
                                Complete={() => updateOrderStatus(item._id, 'completed')}
                                billingStatus={item.billingStatus}
                                status={item.status}
                                orderlocation={getLocationName(item, item?.location?.text || 'Location not provided')}
                                showMarkButton={true}
                                onViewDetails={() => setModalOrder({
                                    ...item,
                                    _status: item.status,
                                    _billingStatus: item.billingStatus,
                                    _location: getLocationName(item, item?.location?.text || 'Location not provided'),
                                })}
                            />
                        ))}
                    </div>
                )}

                {/* ════════════════════════════
                    COMPLETED ORDERS
                ════════════════════════════ */}
                <div style={{ opacity: 0.9 }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#555', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        ✓ Completed
                        <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '100px', background: '#e8f5e9', color: '#2e7d32' }}>
                            {filteredPast.length} {filteredPast.length === 1 ? 'order' : 'orders'}
                        </span>
                    </div>

                    {loading ? <LoadingSkeleton /> : filteredPast.length === 0 ? (
                        <NoDataView />
                    ) : viewMode === 'list' ? (
                        /* LIST VIEW */
                        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #eee' }}>
                                        {['Order ID', 'Location', 'Vehicle', 'Service', 'Time', 'Amount', 'Status', 'Handled By', ''].map((h, i) => (
                                            <th key={i} style={{ fontSize: '9px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em', padding: '8px 10px', textAlign: i >= 6 ? 'center' : 'left' }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPast.map((item, idx) => (
                                        <CompletedOrderRow
                                            key={item._id}
                                            data={item}
                                            billingStatus={item.billingStatus}
                                            status={item.status}
                                            orderlocation={getLocationName(item, item?.location?.text || 'Location not provided')}
                                            onViewDetails={() => setModalOrder({
                                                ...item,
                                                _status: item.status,
                                                _billingStatus: item.billingStatus,
                                                _location: getLocationName(item, item?.location?.text || 'Location not provided'),
                                            })}
                                            style={{ background: idx % 2 === 1 ? '#fdfcff' : '#fff' }}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        /* CARD VIEW */
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                            {filteredPast.map((item) => (
                                <CompletedOrderCard
                                    key={item._id}
                                    data={item}
                                    billingStatus={item.billingStatus}
                                    status={item.status}
                                    orderlocation={getLocationName(item, item?.location?.text || 'Location not provided')}
                                    onViewDetails={() => setModalOrder({
                                        ...item,
                                        _status: item.status,
                                        _billingStatus: item.billingStatus,
                                        _location: getLocationName(item, item?.location?.text || 'Location not provided'),
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </div>
                {modalOrder && (
    <OrderDetailModal
        visible={!!modalOrder}
        onHide={() => setModalOrder(null)}
        data={modalOrder}
        billingStatus={modalOrder._billingStatus}
        status={modalOrder._status}
        orderlocation={modalOrder._location || 'Location not provided'}
        onMarkComplete={() => updateOrderStatus(modalOrder._id, 'completed')}
        onAccept={() => updateOrderStatus(modalOrder._id, 'inprocess')}
        onRefuse={() => updateOrderStatus(modalOrder._id, 'cancelled')}
    />
)}

            </div>
        </div>
    );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
const PulseDot = () => (
    <div style={{
        width: '8px', height: '8px', borderRadius: '50%', background: '#e53935', flexShrink: 0,
        animation: 'pulse-dot 1.4s ease-in-out infinite',
    }} />
);

// Inject pulse keyframe once
if (typeof document !== 'undefined' && !document.getElementById('pulse-dot-style')) {
    const style = document.createElement('style');
    style.id = 'pulse-dot-style';
    style.textContent = `@keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }`;
    document.head.appendChild(style);
}

const LoadingSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
        {[0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" width="32%" height={80} sx={{ borderRadius: 2 }} />)}
    </Box>
);

const NoDataView = () => (
    <div style={{ width: '100%', justifyContent: 'center', display: 'flex', padding: '20px' }}>
        <img src={NoDataImage} alt="No Data" style={{ maxWidth: '200px', opacity: 0.7 }} />
    </div>
);

export default OrderDashboard;
